import { useTranslation, Trans } from 'react-i18next';
import { Input, Button, Banner } from 'components/ui';
import { changePassword } from 'services/auth';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const validatePassword = (password) => {
	const validationRules = [
		(pwd) => pwd.length >= 8,
		(pwd) => /[a-zåäö]/.test(pwd),
		(pwd) => /[A-ZÅÄÖ]/.test(pwd),
		(pwd) => /[0-9]/.test(pwd),
	];

	return validationRules.every((rule) => rule(password));
};

const AuthChangePasswordPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	document.title = `${t('general.app_name')} | ${t('general.change_password')}`;

	const isValid = useMemo(() => {
		const valid = validatePassword(password);
		const identical = password === confirmPassword;

		return identical && valid;
	}, [password, confirmPassword]);

	const handleSubmit = async () => {
		setIsLoading(true);
		const response = await changePassword(password);

		// response can be true or false, if true, the password was changed successfully
		if (response) {
			navigate('/');
			toast.success(t('auth.change_password_success'), { autoClose: 3000 });
			setError(null);
		} else {
			setError('auth.change_password_error');
		}
		setIsLoading(false);
	};
	return (
		<div className='flex flex-col flex-1 justify-center items-center scroll-y'>
			<div className='flex flex-col max-w-md w-full'>
				<h3 className='text-3xl font-bold '>
					<Trans i18nKey='auth.change_password_title' />
				</h3>
				<p className='text-sm text-gray-500 mt-3'>
					<Trans i18nKey='auth.change_password_description' />
				</p>
				<div className='flex flex-col mt-10'>
					<form onSubmit={(e) => e.preventDefault()}>
						<Input
							label={t('auth.new_password')}
							type='password'
							icon='ri-lock-line'
							placeholder={t('auth.new_password_placeholder')}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							errorText={t('auth.new_password_error')}
							validationFn={validatePassword}
						/>
						<Input
							label={t('auth.confirm_password')}
							type='password'
							icon='ri-lock-line'
							placeholder={t('auth.confirm_password_placeholder')}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							errorText={t('auth.confirm_password_error')}
							validationFn={(value) => value === password}
						/>
						{error && (
							<Banner
								label={t(error, { app_name: t('general.app_name') })}
								variant='error'
								className='mb-4'
							/>
						)}
					</form>
					<Button
						className='mt-4'
						type='solid'
						size='large'
						variant='primary'
						disabled={!isValid}
						loading={isLoading}
						onClick={handleSubmit}
					>
						<Trans i18nKey='auth.change_password' />
					</Button>
					<Button
						className='mt-10 text-left w-fit'
						type='text'
						size='medium'
						variant='secondary'
						onClick={() => {
							navigate('/');
							setError(null);
						}}
					>
						<i className='ri-arrow-left-line hover:text-decoration-none' />
						<Trans i18nKey='general.go_back' />
					</Button>
				</div>
			</div>
		</div>
	);
};

export default AuthChangePasswordPage;
